<template>
  <tr v-if="!compact" class="row" :class="{ 'is-inactive': !tag.isActive }">
    <td class="cell-name">
      <div class="name-cell">
        <TagChip :name="tag.name" :color="tag.color" :icon="tag.icon" size="sm" />
        <span v-if="!tag.isActive" class="status-badge status-inactive">Disabled</span>
      </div>
    </td>
    <td class="cell-group">{{ tag.group || '—' }}</td>
    <td class="cell-actions">
      <DisplayOverlayWindowButton ref="detailsWindow" buttonText="Details" windowTitle="Tag">
        <TagDetails :tag="tag" :editable="isEditable" :saving="isUpdating"
          @save="saveEdit" @toggle-active="toggleActive" />
      </DisplayOverlayWindowButton>
    </td>
  </tr>

  <article v-else class="card" :class="{ 'is-inactive': !tag.isActive }">
    <div class="card-head">
      <TagChip :name="tag.name" :color="tag.color" :icon="tag.icon" size="sm" />
      <span v-if="!tag.isActive" class="status-badge status-inactive">Disabled</span>
    </div>
    <p v-if="tag.group" class="card-group">Group: {{ tag.group }}</p>
    <div class="card-actions">
      <DisplayOverlayWindowButton ref="detailsWindow" buttonText="Details" windowTitle="Tag">
        <TagDetails :tag="tag" :editable="isEditable" :saving="isUpdating"
          @save="saveEdit" @toggle-active="toggleActive" />
      </DisplayOverlayWindowButton>
    </div>
  </article>
</template>

<script setup>
import { defineOptions, defineProps, defineEmits, computed, ref } from 'vue';
import UpdateTagModel from '@/domain/tag/UpdateTagModel';
import TagService from '@/services/TagService';
import DisplayOverlayWindowButton from '@/components/buttons/DisplayOverlayWindowButton.vue';
import TagChip from '@/components/tags/TagChip.vue';
import TagDetails from './TagDetails.vue';
import { useOrganizationStore } from '@/store/organizationStore';
import { usePermissions } from '@/composable/usePermissions';
import { Permissions } from '@/constants/permissions';

defineOptions({ name: 'TagTableItem' });

const props = defineProps({
  tag: Object,
  compact: { type: Boolean, default: false },
});

const emits = defineEmits(['onTagModified']);

const organizationStore = useOrganizationStore();
const { can } = usePermissions();
const detailsWindow = ref(null);
const isUpdating = ref(false);

// Only org admins can edit / rename / recolour / disable tags (reuses the org-admin permission).
const isEditable = computed(() => can(Permissions.OrganizationAdminView));

// Save a rename / recolour / regroup / icon / description change from the details editor.
const saveEdit = async (changes) => {
  await persist({ isActive: props.tag.isActive, ...changes });
};

// Toggle disabled state, keeping the current name/colour/group/icon/description.
const toggleActive = async () => {
  await persist({ isActive: !props.tag.isActive });
};

const persist = async (changes) => {
  isUpdating.value = true;
  const record = new UpdateTagModel(
    props.tag.id,
    organizationStore.organizationId,
    changes.name ?? props.tag.name,
    changes.isActive,
    changes.color ?? props.tag.color,
    (changes.group !== undefined ? changes.group : props.tag.group) || null,
    changes.description !== undefined ? changes.description : props.tag.description,
    changes.icon !== undefined ? changes.icon : props.tag.icon,
  );
  try {
    await TagService.updateTag(record);
  } finally {
    isUpdating.value = false;
    emits('onTagModified');
    detailsWindow.value?.closeWindow();
  }
};
</script>

<style scoped>
.row { transition: background 0.15s ease; border-bottom: 1px solid rgba(255, 255, 255, 0.04); }
.row:last-child { border-bottom: none; }
.row:hover { background: rgba(100, 255, 218, 0.04); }
.row.is-inactive .cell-name, .row.is-inactive .cell-group { opacity: 0.5; }
.row td { padding: 12px 16px; vertical-align: middle; font-size: 0.92rem; }

.cell-name { color: #fff; font-weight: 600; }
.cell-group { color: #b0bec5; }
.cell-actions { width: 120px; text-align: right; white-space: nowrap; }

.name-cell { display: flex; align-items: center; gap: 10px; min-width: 0; }

.card {
  flex: 0 0 auto; display: flex; flex-direction: column; gap: 8px; padding: 14px;
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.05); border-radius: 12px;
}
.card.is-inactive .card-head, .card.is-inactive .card-group { opacity: 0.6; }
.card-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.card-group { font-size: 0.85rem; color: #b0bec5; margin: 0; }
.card-actions { display: flex; justify-content: flex-end; }

.status-badge {
  display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 999px;
  font-size: 0.7rem; font-weight: 700; letter-spacing: 0.02em;
}
.status-inactive {
  background: rgba(239, 68, 68, 0.12); border: 1px solid rgba(239, 68, 68, 0.3); color: #f87171;
}
</style>
